import { useCallback } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { MAIL_VIEWS, type MailView } from "./mailTypes.js";

const FOLDERS = new Set<MailView>(MAIL_VIEWS.map((entry) => entry.id));

export function useMailLocation() {
  const { messageId } = useParams<{ messageId?: string }>();
  const [search, setSearch] = useSearchParams();
  const navigate = useNavigate();
  const requestedFolder = search.get("folder") as MailView | null;
  const folder: MailView = requestedFolder && FOLDERS.has(requestedFolder) ? requestedFolder : "inbox";
  /** Kennung des Postfachs beim Mailserver; ohne Angabe das eigene. */
  const accountId = search.get("account");
  const query = search.get("query") ?? "";
  const compose = search.get("compose") === "1";
  const draftId = search.get("draft");
  const replyTo = search.get("reply");

  const update = useCallback((values: Record<string, string | null>) => {
    setSearch((current) => {
      const next = new URLSearchParams(current);
      for (const [key, value] of Object.entries(values)) {
        if (value) next.set(key, value);
        else next.delete(key);
      }
      return next;
    });
  }, [setSearch]);

  const navigateTo = useCallback((id: string | null, values: Record<string, string | null> = {}) => {
    const next = new URLSearchParams(search);
    for (const [key, value] of Object.entries(values)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    const suffix = next.toString();
    navigate(`${id ? `/mail/${id}` : "/mail"}${suffix ? `?${suffix}` : ""}`);
  }, [navigate, search]);

  const openMessage = useCallback((id: string | null) => {
    navigateTo(id);
  }, [navigateTo]);

  return {
    messageId: messageId ?? null,
    folder,
    accountId,
    query,
    compose,
    draftId,
    replyTo,
    openMessage,
    openFolder: (next: MailView) => navigateTo(null, { folder: next === "inbox" ? null : next, panel: "content" }),
    openAccountFolder: (account: string, next: MailView) => navigateTo(null, { account, folder: next === "inbox" ? null : next, panel: "content", query: null }),
    selectAccount: (next: string) => navigateTo(null, { account: next, folder: null }),
    setQuery: (next: string) => update({ query: next || null }),
    openComposer: (options: { draft?: string; reply?: string } = {}) => update({ compose: "1", draft: options.draft ?? null, reply: options.reply ?? null }),
    closeComposer: () => update({ compose: null, draft: null, reply: null }),
  };
}
